/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import { useEffect, useRef, useState } from 'react';
import './counter.css';

interface DigitCounterProps {
  value: number;
}

function DigitCounter({ value }: DigitCounterProps) {
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [nextValue, setNextValue] = useState<number | null>(null);
  const digitRef = useRef<HTMLSpanElement>(null);
  const topRef = useRef<HTMLElement>(null);
  const bottomRef = useRef<HTMLElement>(null);
  const backRef = useRef<HTMLElement>(null);
  const backBottomRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (currentValue === null) {
      setCurrentValue(value);
      if (topRef.current) topRef.current.innerText = value.toString();
      if (bottomRef.current) bottomRef.current.setAttribute('data-value', value.toString());
      if (backRef.current) backRef.current.setAttribute('data-value', value.toString());
      if (backBottomRef.current) backBottomRef.current.setAttribute('data-value', value.toString());
    }
  }, []);

  // Set up animation event listener
  useEffect(() => {
    const backElement = backRef.current;

    const handleAnimationEnd = (e: AnimationEvent) => {
      // The top flip animation has ended
      if (e.animationName === 'flipTop' && nextValue !== null) {
        // Now it's safe to update the top element
        if (topRef.current) topRef.current.innerText = nextValue.toString();
        setCurrentValue(nextValue);
        setNextValue(null);
        setAnimating(false);
      }
    };

    if (backElement) {
      backElement.addEventListener('animationend', handleAnimationEnd as EventListener);
    }

    return () => {
      if (backElement) {
        backElement.removeEventListener('animationend', handleAnimationEnd as EventListener);
      }
    };
  }, [nextValue]);

  // Handle value change and animation
  useEffect(() => {
    if (currentValue !== null && value !== currentValue && !animating) {
      if (!digitRef.current) return;

      // Start the animation process
      setAnimating(true);
      setNextValue(value);

      // Step 1: Keep displaying the current/old value
      if (topRef.current) topRef.current.innerText = currentValue.toString();
      if (bottomRef.current) bottomRef.current.setAttribute('data-value', currentValue.toString());
      if (backRef.current) backRef.current.setAttribute('data-value', currentValue.toString());

      // Step 2: Set up the new value only on the back bottom (hidden until flip)
      if (backBottomRef.current) backBottomRef.current.setAttribute('data-value', value.toString());

      // Step 3: Trigger the flip animation
      digitRef.current.classList.remove('flip');
      void digitRef.current.offsetWidth; // Force reflow
      digitRef.current.classList.add('flip');
    }
  }, [value, currentValue, animating]);

  return (
    <span ref={digitRef} className="flip-countdown__digit mx-0.5 inline-block text-center">
      <b className="card relative block pb-[0.72em] text-[1.1rem] leading-[0.95] md:text-[2rem]">
        <b ref={topRef} className="card__top block bg-gradient-to-b from-[#ffffff] to-[#d0d0d0] text-center"></b>
        <b
          ref={bottomRef}
          className="card__bottom absolute left-0 top-1/2 overflow-hidden border-t border-[#a0a0a0] bg-gradient-to-b from-[#d0d0d0] to-[#a0a0a0] text-center"
          data-value={currentValue !== null ? currentValue : ''}
        ></b>
        <b
          ref={backRef}
          className="card__back pointer-events-none absolute left-0 top-0 h-full"
          data-value={currentValue !== null ? currentValue : ''}
        >
          <b
            ref={backBottomRef}
            className="card__bottom absolute left-0 top-1/2 overflow-hidden border-t border-[#a0a0a0] bg-gradient-to-b from-[#d0d0d0] to-[#a0a0a0] text-center"
            data-value={currentValue !== null ? currentValue : ''}
          ></b>
        </b>
      </b>
    </span>
  );
}

export function Counter() {
  // Calculate seconds since the reference date
  const calculateInitialCount = () => {
    // number scans on 22nd April 2025
    const baseCount = 121_446_030;
    const now = new Date();
    const referenceDate = new Date(2025, 3, 22);
    const secondsSinceReference = Math.floor((now.getTime() - referenceDate.getTime()) / 1000);
    return baseCount + Math.floor(secondsSinceReference * 0.75);
  };

  const [count, setCount] = useState(calculateInitialCount());
  const lastUpdateTime = useRef(performance.now());
  const nextUpdateInterval = useRef(Math.random() * 2000 + 1000); // Random interval between 1-3 seconds
  const incrementBy = 1;

  useEffect(() => {
    const updateCounter = () => {
      const currentTime = performance.now();
      if (currentTime - lastUpdateTime.current >= nextUpdateInterval.current) {
        setCount(prev => prev + incrementBy);
        lastUpdateTime.current = currentTime;
        // Set a new random interval for the next update (between 1-3 seconds)
        nextUpdateInterval.current = Math.random() * 2000 + 1000;
      }
      requestAnimationFrame(updateCounter);
    };

    const animationFrame = requestAnimationFrame(updateCounter);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  const countString = count.toString();
  const formattedDigits = [];

  // Group digits with spacing between each group of 3
  for (let i = 0; i < countString.length; i++) {
    formattedDigits.push(<DigitCounter key={`digit-${i}`} value={parseInt(countString[i])} />);

    // Add spacing after each group of 3 digits from the right
    const remainingDigits = countString.length - 1 - i;
    if (remainingDigits % 3 === 0 && remainingDigits > 0) {
      formattedDigits.push(<span key={`space-${i}`} className="mx-1"></span>);
    }
  }

  return (
    <div className="counter__gradient-bg grid gap-2 p-1 text-center md:p-2">
      <div className="flip-countdown">{formattedDigits}</div>
      <p className="counter__gradient-text-bg text-lg">builds scanned</p>
    </div>
  );
}
